/**
 * TableShell — the non-error table shell (colgroup, sortable head, body,
 * footer). Split out of FmTable.render so neither module exceeds the size gate.
 */
import React from 'react';
import type { FmTableColumn, FmTableProps } from '../../types/fm-table';
import type { FmTableInternalRow, SortState } from './FmTable.utils';
import {
  TableHeaderCell,
  TableBodyRows,
  TableFooter,
  type FmTableA11yLabels,
} from './FmTable.render';

export interface TableShellProps<T extends object> {
  hostRef: React.RefObject<HTMLDivElement | null>;
  tableClass: string;
  style?: React.CSSProperties;
  loading: boolean;
  loadingTestId: string;
  dataTestId: string;
  caption: React.ReactNode;
  tableId: string;
  columns: FmTableColumn<T>[];
  showExpandColumn: boolean;
  totalColumnCount: number;
  usesLabelledCells: boolean;
  headerRowTestId?: string;
  headTestId?: string;
  expandable: FmTableProps<T>['expandable'];
  canSort: boolean;
  effectiveSortState: SortState;
  a11yLabels: FmTableA11yLabels;
  onSort: (col: FmTableColumn<T>) => void;
  internalRows: FmTableInternalRow<T>[];
  emptyText?: React.ReactNode;
  groupHeader?: FmTableProps<T>['groupHeader'];
  groupCountLabel?: FmTableProps<T>['groupCountLabel'];
  renderDataRow: (record: Extract<FmTableInternalRow<T>, { kind: 'data' }>) => React.ReactNode;
  renderCell: (row: T, col: FmTableColumn<T>) => React.ReactNode;
  footerRow?: T;
  summaryContent: React.ReactNode;
  footerRowClassName?: FmTableProps<T>['footerRowClassName'];
  footerRowTestId?: FmTableProps<T>['footerRowTestId'];
}

/** Non-error table shell: colgroup, sortable head, body, and footer. */
export function TableShell<T extends object>(props: TableShellProps<T>): React.ReactElement {
  const {
    hostRef,
    tableClass,
    style,
    loading,
    loadingTestId,
    dataTestId,
    caption,
    tableId,
    columns,
    showExpandColumn,
    totalColumnCount,
    usesLabelledCells,
    headerRowTestId,
    headTestId,
    expandable,
    canSort,
    effectiveSortState,
    a11yLabels,
    onSort,
    internalRows,
    emptyText,
    groupHeader,
    groupCountLabel,
    renderDataRow,
    renderCell,
    footerRow,
    summaryContent,
    footerRowClassName,
    footerRowTestId,
  } = props;

  return (
    <div
      ref={hostRef}
      className={tableClass}
      data-testid={loading ? loadingTestId : dataTestId}
      data-fm-table-state={loading ? 'loading' : undefined}
      style={style}
    >
      <table className="fm-table__table" aria-busy={loading || undefined}>
        {caption ? (
          <caption
            id={`${tableId}-caption`}
            className="fm-table__caption"
            data-testid="fm-table-caption"
          >
            {caption}
          </caption>
        ) : null}
        <colgroup>
          {showExpandColumn ? <col className="fm-table__expand-col" /> : null}
          {columns.map((col) => (
            <col key={col.key} className="fm-table__col" />
          ))}
        </colgroup>
        <thead className="fm-table__head" data-testid={headTestId}>
          <tr data-testid={headerRowTestId}>
            {showExpandColumn ? (
              <th
                scope="col"
                className="fm-table__cell fm-table__header-cell fm-table__expand-cell"
                aria-label={expandable?.columnTitle ? undefined : a11yLabels.expandRow}
              >
                {expandable?.columnTitle}
              </th>
            ) : null}
            {columns.map((col) => (
              <TableHeaderCell
                key={col.key}
                col={col}
                canSort={canSort}
                effectiveSortState={effectiveSortState}
                a11yLabels={a11yLabels}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody className="fm-table__body">
          <TableBodyRows
            a11yLabels={a11yLabels}
            loading={loading}
            internalRows={internalRows}
            columns={columns}
            totalColumnCount={totalColumnCount}
            showExpandColumn={showExpandColumn}
            usesLabelledCells={usesLabelledCells}
            emptyText={emptyText}
            groupHeader={groupHeader}
            groupCountLabel={groupCountLabel}
            renderDataRow={renderDataRow}
          />
        </tbody>
        <TableFooter
          a11yLabels={a11yLabels}
          loading={loading}
          footerRow={footerRow}
          summaryContent={summaryContent}
          totalColumnCount={totalColumnCount}
          showExpandColumn={showExpandColumn}
          columns={columns}
          footerRowClassName={footerRowClassName}
          footerRowTestId={footerRowTestId}
          renderCell={renderCell}
        />
      </table>
    </div>
  );
}
